import { MouseEventHandler, useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useReselector } from '@/utils/sharedHooks'
import { TypographyStyles } from '@/styles'

import moment from 'moment'
import { useAppDispatch } from '@/utils/typeHelpers'
import { DATE_FORMATS } from '@/utils/dateHelpers'

import { formatCurrency, centsToDollars } from '@/utils/currencyHelpers'
import {
  editAllocationGuide,
  fetchAllocationGuidesSummary,
  fetchAllocationGuidesIfNeeded,
} from '@/src/actions/allocationGuideActions'
import { getAllocationGuideById } from '@/src/selectors/allocationGuideSelectors'
import { AllocationGuideAmountsTable } from './AllocationGuideAmountsTable'
import {
  Alert,
  Button,
  Card,
  Dialog,
  Text,
  Separator,
  Breadcrumb,
  CardColors,
} from '@/ui'
import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'

import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import { PageHeader } from '@/components/PageHeader'

import { markUserActionItemCompleteIfExists } from '@/features/user/dashboard/lib/service'
import { UserActionItemActionItemIdentifiers } from '@/features/user/dashboard/user-action-items/lib/userActionItems.slice'

// TODO: Migrate to V2
import { Loader } from 'semantic-ui-react'

export const SingleAllocationGuidePanel = () => {
  const dispatch = useAppDispatch()
  const { allocationGuideId } = useParams<{
    allocationGuideId: string | undefined
  }>()

  const allocationGuide = useReselector(
    getAllocationGuideById,
    allocationGuideId
  )

  const pageView = useAnalyticsView()
  const track = useAnalyticsTrack()
  const navigate = useNavigate()

  useEffect(() => {
    pageView('allocation guide report')
  }, [pageView])

  const [loading, setLoading] = useState(true)

  const markActionItemsComplete = useCallback(async () => {
    if (allocationGuide) {
      const dateParts = allocationGuide?.date.split('-')

      // Make remove leading 0 if exists
      const month =
        dateParts[1] && dateParts[1][0] === '0'
          ? dateParts[1].substring(1)
          : dateParts[1]

      await markUserActionItemCompleteIfExists(
        UserActionItemActionItemIdentifiers.reviewAllocationGuide({
          month,
          year: dateParts[0],
        }),
        (event, properties) => track(event, properties)
      )
    }
  }, [allocationGuide, track])

  useEffect(() => {
    const fetch = async () => {
      await dispatch(fetchAllocationGuidesIfNeeded())
      await markActionItemsComplete()
      setLoading(false)
    }
    fetch()
  }, [dispatch, markActionItemsComplete])

  const handleUpdateAllocationGuide: MouseEventHandler = () => {
    if (allocationGuide) {
      dispatch(editAllocationGuide(allocationGuide?.id))
      dispatch(fetchAllocationGuidesSummary())
    }
  }

  const renderBody = () => {
    if (loading) {
      return <Loader inline active />
    }

    const profits =
      centsToDollars(allocationGuide?.incomeAtCompilationInCents) +
      centsToDollars(allocationGuide?.expensesAtCompilationInCents)

    return (
      <Card color={CardColors.LEVEL_1} className="flex flex-col gap-4 w-full">
        <Text variant={TypographyStyles.H2}>
          {moment(allocationGuide?.date).format(DATE_FORMATS.MONTH_YEAR)}{' '}
          Allocation Guide
        </Text>
        <div className="flex items-center justify-between">
          <Text variant={TypographyStyles.BODY_SM}>
            Compiled on{' '}
            {moment(allocationGuide?.compiledOn).format(
              DATE_FORMATS.DISPLAY_LONG
            )}
          </Text>

          <Dialog>
            <Dialog.Trigger asChild>
              <Button variant="link" size="md" className="ml-4">
                Update
              </Button>
            </Dialog.Trigger>
            <Dialog.Content>
              <Dialog.Title>Confirm Allocation Guide Update</Dialog.Title>
              <Text variant={TypographyStyles.BODY_MD}>
                Please confirm that you would like to make changes to the
                selected allocation guide. Changes will be based on the
                currently set percentages. Would you like to continue?
              </Text>
              <Dialog.Footer>
                <Dialog.Close asChild>
                  <Button variant="secondary">Cancel</Button>
                </Dialog.Close>
                <Dialog.Close asChild>
                  <Button onClick={handleUpdateAllocationGuide}>
                    Confirm Changes
                  </Button>
                </Dialog.Close>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog>
        </div>
        {profits < 0 && (
          <Alert
            variant="default"
            description="When your monthly profit is negative, all allocations will be set to $0.00"
          />
        )}

        <Separator />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Side – Table */}
          <div className="lg:col-span-7 w-full">
            <AllocationGuideAmountsTable
              allocationGuide={allocationGuide}
              profits={profits}
            />
          </div>

          {/* Right Side – P&L Summary Card */}
          <div className="lg:col-span-5">
            <Card color={CardColors.LEVEL_1} className="p-4">
              <Text variant={TypographyStyles.H1} className="mb-2">
                Profit and Loss*
              </Text>
              <Text variant={TypographyStyles.BODY_SM}>
                <i>*at time of allocation guide creation</i>
              </Text>

              <div className="flex justify-between mb-2 mt-4">
                <Text variant={TypographyStyles.BODY_MD}>Income</Text>
                <Text variant={TypographyStyles.BODY_MD}>
                  <CurrencyFormatLabel
                    value={formatCurrency(
                      centsToDollars(
                        allocationGuide?.incomeAtCompilationInCents
                      )
                    )}
                  />
                </Text>
              </div>

              <div className="flex justify-between mb-2">
                <Text variant={TypographyStyles.BODY_MD}>Expenses</Text>
                <Text variant={TypographyStyles.BODY_MD}>
                  <CurrencyFormatLabel
                    value={formatCurrency(
                      centsToDollars(
                        allocationGuide?.expensesAtCompilationInCents
                      )
                    )}
                  />
                </Text>
              </div>

              <div className="flex justify-between mt-4">
                <Text variant={TypographyStyles.BODY_MD}>Profit</Text>
                <Text variant={TypographyStyles.BODY_MD}>
                  <CurrencyFormatLabel value={formatCurrency(profits)} />
                </Text>
              </div>
            </Card>
          </div>
        </div>
      </Card>
    )
  }

  return (
    <>
      <PageHeader header="Allocation Guides" />
      <Breadcrumb className="mt-4">
        <Breadcrumb.List>
          <Breadcrumb.Item>
            <Breadcrumb.Link
              onClick={() => {
                navigate('/allocation-guides')
              }}
            >
              All Allocations
            </Breadcrumb.Link>
          </Breadcrumb.Item>
          <Breadcrumb.Separator />
          <Breadcrumb.Item>
            <Breadcrumb.Page>
              {moment(allocationGuide?.date).format(DATE_FORMATS.MONTH_YEAR)}
            </Breadcrumb.Page>
          </Breadcrumb.Item>
        </Breadcrumb.List>
      </Breadcrumb>
      {allocationGuide && renderBody()}
    </>
  )
}
